import { create } from 'zustand';
import AsyncStorage from '@react-native-async-storage/async-storage';
import type { PlanDrawKind, DefenseStrategy, Structure, UserRole } from '../types';
import { useDefensePlansStore } from './defensePlansStore';
import type { PlanDropTarget, PlanDropOutcome } from '../utils/planListDrop';
import type { PlanCompanion } from '../utils/programMoves';
import { useMapOverlaysStore } from './mapOverlaysStore';
import { useAreaContactsStore } from './areaContactsStore';
import { usePeopleListsStore } from './peopleListsStore';
import { useVendorsStore } from './vendorsStore';
import { usePlanMapPlacementStore } from './planMapPlacementStore';
import { useFieldMode } from '../hooks/useFieldMode';

const RECENTLY_VIEWED_CAP = 5;

const SAVE_NUDGE_KEY = 'redi:saveNudgeDismissed:v1';
const TOUR_SEEN_KEY = 'redi:tourSeen:v1';
const ROLES_NUDGE_KEY = 'redi:rolesNudgeDismissed:v1';
const COACHMARK_KEY = 'redi:createCoachmarkSeen:v1';
const OBSERVATION_GUIDANCE_KEY = 'redi:observationGuidanceSeen:v1';
const MAP_STYLE_KEY = 'redi:mapStyle:v1';
const MAP_PATHS_KEY = 'redi:mapPathsEngaged:v1';
const PENDING_PROPERTY_KEY = 'redi:pendingProperty:v1';
const PREVIEW_EVALUATION_KEY = 'redi:previewEvaluation:v1';
const PENDING_AUDIENCE_KEY = 'redi:pendingAudience:v1';
const PENDING_MODEL_KEY = 'redi:pendingModel:v1';

// --- Insurer-link journey state (sessionStorage-backed on web) -------------
// Both objects survive a full page reload (Google sign-in popup fallback,
// a hard refresh mid-sign-up) but not a new browser session — they describe
// one visit that started from an insurer email link, not the device.
// sessionStorage is synchronous, so unlike the AsyncStorage flags above these
// hydrate inline at store creation with no null-until-hydrated dance.
// Native has no reload-during-auth path, so in-memory state is enough there.
function readSessionJson<T>(key: string): T | null {
  try {
    if (typeof sessionStorage === 'undefined') return null;
    const raw = sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeSessionJson(key: string, value: unknown): void {
  try {
    if (typeof sessionStorage === 'undefined') return;
    if (value == null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // non-fatal — in-memory state still works for this page's lifetime
  }
}

/**
 * A property primed by an insurer deep link (`/?property=<structureId>&
 * model=<checklistId>`). The Property tab and MapPathsOverlay's Property
 * card route here before falling back to the user's claimed structure.
 */
export interface PendingProperty {
  structureId: string;
  checklistId?: string;
}

/**
 * A signed-out visitor's local checklist ticks on /structure/[id] (the
 * try-then-save preview). Held here until the visitor creates an account;
 * the structure screen then claims the structure with this model and seeds
 * the ticks as real progress. `saveRequested` marks that the visitor tapped
 * the save CTA — the auto-claim effect only fires on an explicit ask, so a
 * sign-in reached some other way never claims a property behind their back.
 */
export interface PreviewEvaluation {
  structureId: string;
  checklistId: string;
  /** True when the previewed model is the stakeholder-type default —
   *  claims encode "default model" as an absent insurerChecklistId. */
  checklistIsDefault: boolean;
  totalCount: number;
  itemIds: string[];
  saveRequested: boolean;
}

// Stored JSON is same-session and same-origin, but a deploy can change the
// shape mid-session — validate the load-bearing fields before trusting it.
function hydratePendingProperty(): PendingProperty | null {
  const v = readSessionJson<PendingProperty>(PENDING_PROPERTY_KEY);
  return v && typeof v.structureId === 'string' ? v : null;
}

function hydratePreviewEvaluation(): PreviewEvaluation | null {
  const v = readSessionJson<PreviewEvaluation>(PREVIEW_EVALUATION_KEY);
  return v &&
    typeof v.structureId === 'string' &&
    typeof v.checklistId === 'string' &&
    Array.isArray(v.itemIds)
    ? v
    : null;
}

function hydratePendingModel(): string | null {
  const v = readSessionJson<string>(PENDING_MODEL_KEY);
  return typeof v === 'string' && v.length > 0 ? v : null;
}

function hydratePendingAudience(): UserRole | null {
  const v = readSessionJson<UserRole>(PENDING_AUDIENCE_KEY);
  return typeof v === 'string' ? v : null;
}

/**
 * Basemap behind the map screen. 'standard' is the near-monochrome
 * illustrated map, 'satellite' is photographic imagery with roads and place
 * names over it. Tile sources for web live in components/map/basemaps.ts;
 * native maps this onto the Google Maps SDK's own mapType.
 */
export type MapStyle = 'standard' | 'satellite';

export type ActiveSheet =
  | null
  | 'addProperty'
  | 'createMenu'
  | 'howItWorks'
  | 'mapLayers'
  | 'userMenu'
  | 'upgradeAccount'
  | 'propertyPicker'
  | 'observationGuidance'
  // A moderator's plan-map-overlay import flow (Sept 2026): pick an image,
  // set its title/source, then hand off to the corner-placement editor.
  // Mounted by a later phase; the sheet name lives here now so this phase's
  // uiStore surface is stable for that UI to build on.
  | 'planMapImport';
// 'rail' (Sept 2026 A2): the desktop-shell map route's avatar trigger sits
// at the bottom of the rail, under the map tools tray — UserMenuDropdown
// positions from there instead of the footer for this one anchor. Every
// other desktop screen still uses the footer position regardless of anchor
// (see UserMenuDropdown's isDesktopShell branch).
export type UserMenuAnchor = 'header' | 'mapCorner' | 'rail';

/** One registered docked panel: its width and how far from its edge it starts. */
export interface DockInset {
  width: number;
  offset: number;
}

export interface ToastAction {
  label: string;
  onPress: () => void;
}

export interface ToastItem {
  id: string;
  message: string;
  action?: ToastAction;
  durationMs: number;
  /** Runs once when the toast leaves for any reason: timeout, dismiss,
   *  Escape, Undo press, or a newer toast replacing it. */
  onClose?: () => void;
}

interface OpenSheetOpts {
  addPropertyInitialQuery?: string;
  /**
   * Where the user-menu dropdown should anchor. The trigger moves around:
   * top-right header avatar on most screens ('header'), the bottom-right
   * corner stack on the mobile map ('mapCorner'), the bottom of the rail on
   * the desktop map route ('rail'). Desktop shell ignores 'header'/
   * 'mapCorner' (the sidebar/rail owns the trigger there) but honors
   * 'rail' specifically, since that's the one desktop case where the
   * trigger isn't in its usual footer spot.
   */
  userMenuAnchor?: UserMenuAnchor;
}

interface UIState {
  tabBarVisible: boolean;
  _hideCount: number;
  showTabBar: () => void;
  hideTabBar: () => void;

  // Only one sheet may be open at a time. The map's "+" button, the (?)
  // explainer, the user-menu trigger, and the global Add-Property flow
  // all route through openSheet/closeSheet — opening one implicitly
  // closes any other.
  activeSheet: ActiveSheet;
  // Monotonic counter incremented on every openSheet call. Sheet
  // components subscribe to this alongside their `activeSheet` boolean
  // so their present() useEffect re-fires even when the same sheet is
  // re-opened (e.g. user opened on map, navigated to feed without the
  // modal's onDismiss firing, then tapped the avatar on feed — Zustand
  // would otherwise skip the re-render since the boolean is unchanged).
  sheetOpenTick: number;
  addPropertyInitialQuery: string;
  userMenuAnchor: UserMenuAnchor;
  openSheet: (sheet: NonNullable<ActiveSheet>, opts?: OpenSheetOpts) => void;
  closeSheet: () => void;

  // --- Toast (Tier 2 feel pass, Sept 2026) --------------------------------
  // One visible at a time — a new toast replaces whatever is showing, same
  // "single-active" shape as activeSheet above. ToastHost
  // (src/components/common/Toast.tsx) is mounted once in app/_layout.tsx and
  // renders whichever of these is current. Default auto-dismiss is 4s, or 6s
  // when the toast carries an action (more to read before it's gone).
  toast: ToastItem | null;
  showToast: (opts: {
    message: string;
    action?: ToastAction;
    durationMs?: number;
    onClose?: () => void;
  }) => string;
  dismissToast: (id: string) => void;

  // One-shot map focus request. In-app actions (Show on Map on the
  // structure/post detail screens) can't deliver router params to an
  // already-mounted tab — dismissTo/POP_TO drops them — so they stash the
  // target here and the map screen consumes + clears it. Share links and
  // external deep links still use the ?focusStructureId URL params path.
  // `kind` lets the shared channel carry a structure, a post or a defense
  // plan id without the map's focus consumer having to guess which.
  pendingMapFocus: { kind: 'structure' | 'post' | 'plan'; id: string } | null;
  requestMapFocus: (structureId: string) => void;
  requestMapPostFocus: (postId: string) => void;
  requestMapPlanFocus: (planId: string) => void;
  clearMapFocus: () => void;

  // One-shot: MapLayersSheet's "Present observations" row asks the map to
  // open the fullscreen presentation (MapSlideshow.web.tsx). The sheet is
  // mounted globally (app/_layout.tsx) with no access to the Leaflet
  // instance or its bounds, which Map.web.tsx needs to build the slide
  // deck — so this is a request the map consumes + clears, same shape as
  // pendingMapFocus above. A counter, not a boolean, so a repeat request
  // while the flag hasn't been cleared yet still re-fires.
  slideshowOpenRequestId: number;
  requestSlideshowOpen: () => void;
  clearSlideshowOpenRequest: () => void;
  // One-shot, same shape and reasoning as slideshowOpenRequestId, for the
  // opposite direction: the map-tools rail's Present row (MapRailTools.web)
  // is mounted globally too and needs to ask the map to end the
  // presentation the same way it asks to start one.
  slideshowCloseRequestId: number;
  requestSlideshowClose: () => void;
  clearSlideshowCloseRequest: () => void;
  // Mirrors whether the presentation is up right now (stage !== 'closed'),
  // written by Map.web.tsx — the one place that owns the slideshow
  // controller. The rail's Present row reads this for its active/pressed
  // state instead of tracking the stage itself, which would mean importing
  // the slideshow controller's types into a component with no other reason
  // to know about it. Not reset on sign-out: it describes what the map is
  // doing right now, not session/account state, and Map.web.tsx clears it
  // on unmount regardless.
  presentationActive: boolean;
  setPresentationActive: (active: boolean) => void;

  // --- Docked post panel (desktop map, Sept 2026 phase 2) -----------------
  // The post shown in the right-docked panel (app/(tabs)/index.tsx,
  // testID `post-panel`) when a map marker or a row inside the structure
  // panel opens an observation on the desktop shell — panel-system.md
  // Phase 2's "an observation opened from the map or the property panel
  // becomes a right-dock drill-in". Same right-hand surface as a selected
  // structure and the observation presentation (see Map.web.tsx's dock
  // arbitration comments): opening one of the three clears the other two,
  // done at each opening call site rather than here, matching how
  // handleStructurePress/handleOpenSlideshow already arbitrate. Session-only,
  // reset on sign-out.
  dockedPostId: string | null;
  openDockedPost: (postId: string) => void;
  closeDockedPost: () => void;

  // --- Docked composer (desktop map, panel-system.md Phase 3A) ------------
  // Same right-hand surface as the structure/post/presentation panels and
  // the add-property dock above: gated behind
  // constants/composer.ts's DESKTOP_COMPOSER_DOCK_ENABLED, mounted by
  // ComposerDockPanel.web.tsx (app/(tabs)/index.tsx) only on the desktop
  // shell's map route. Opening it clears the other right-dock surfaces
  // (ComposerDockPanel's own open effect); opening any of THOSE closes this
  // one in turn (handleStructurePress/handlePostPress/handleOpenSlideshow/
  // PropertyDockPanel). Session-only, reset on sign-out.
  composerDockOpen: boolean;
  openComposerDock: () => void;
  closeComposerDock: () => void;
  // The draggable map pin's position while the composer dock is open — set
  // once when the pin layer mounts (map center or the user's location), then
  // updated as the author drags it. Null once the dock closes; the composer
  // reads this instead of opening the location drill-in (panel-system.md
  // Phase 3, "the location is set by dragging a pin on the map").
  composerPin: { latitude: number; longitude: number } | null;
  setComposerPin: (pin: { latitude: number; longitude: number } | null) => void;

  // --- Right-docked panel widths (map chrome coordination) ---------------
  // Every right-docked surface (DockedPanel — structure detail, defense
  // plan editor, observation presentation) registers its width here on
  // mount and drops it on unmount, keyed by a stable id (its testID)
  // instead of one boolean per surface. Floating map chrome that needs to
  // stay clear of whatever is docked (the search bar, MapControls, the
  // post hover card's clamp, focusOnBounds padding) reads useRightDockInset
  // below rather than learning about each panel by name — a new docked
  // surface needs no wiring here to be covered.
  //
  // Each entry is { width, offset }: a companion panel stacks on its parent,
  // so it registers offset = the parent's width and the inset becomes
  // max(offset + width) (docs/plans/panel-system.md). offset defaults to 0,
  // so a plain dock registers exactly as before.
  dockInsets: Record<string, DockInset>;
  registerDockInset: (id: string, width: number, offset?: number) => void;
  unregisterDockInset: (id: string) => void;
  // --- Left-docked panel widths (Sept 2026 — defense plan editor/viewer) ---
  // Same shape and reasoning as dockInsets above, mirrored for the one
  // surface that docks on the LEFT: the defense plan editor and the
  // read-only plan viewer (client ask — "come out from the left side and
  // cover up" the sidebar, so the map gets the sidebar's width back).
  // Keyed by id for the same swap-order-independence reason. Floating map
  // chrome that needs to stay clear of a left-docked panel reads
  // useLeftDockInset below instead of learning about the plan surface by
  // name.
  leftDockInsets: Record<string, DockInset>;
  registerLeftDockInset: (id: string, width: number, offset?: number) => void;
  unregisterLeftDockInset: (id: string) => void;
  // The dock side the user touched last. resolvePanelLayout
  // (utils/panelLayout.ts) folds the OTHER side first when the map runs out
  // of room, so the newest work stays expanded. Opening a dock or clicking
  // its collapsed tab sets it.
  panelNewest: 'left' | 'right';
  setPanelNewest: (side: 'left' | 'right') => void;
  // Bottom edge (px from the map's top) of floating chrome pinned under the
  // search bar, today only the phone plan viewer card. Plan fits pad by it
  // so a feature never lands under the card. 0 when nothing is there.
  mapTopInset: number;
  setMapTopInset: (px: number) => void;

  // --- Map layers panel (desktop web, Sept 2026) --------------------------
  // Session-only, reset on sign-out like the overlay toggles below. Not
  // `activeSheet`: that's single-modal and its dialog path calls
  // hideTabBar(), which would hide MapControls and the Key underneath a
  // panel that's meant to sit beside them, not over them. Deliberately its
  // own boolean so it can be open at the same time as the plan editor (it
  // paints over it — see MapLayersPanel.web's mount-order comment) or the
  // structure/presentation panels on the right.
  layersPanelOpen: boolean;
  toggleLayersPanel: () => void;
  closeLayersPanel: () => void;

  // --- People panel (desktop web, Oct 2026) -------------------------------
  // The signed-in user's uploaded leader lists, left-docked like Layers and
  // mutually exclusive with it (and closed by entering the plan editor or a
  // plan viewer, the same way Layers is). Session-only, reset on sign-out.
  peoplePanelOpen: boolean;
  togglePeoplePanel: () => void;
  closePeoplePanel: () => void;

  // --- Program panel (desktop web, Oct 2026) ------------------------------
  // The area's progress through the ten program steps plus the weekly brief,
  // moderators only. Left-docked like Layers and People and mutually
  // exclusive with both; closed by entering the plan editor or a viewer.
  programPanelOpen: boolean;
  toggleProgramPanel: () => void;
  closeProgramPanel: () => void;
  /** The rail's Vendors button (Glenn, Nov 4 2026: "might be worth having a
   *  vendor link"). Opens the Program panel with Vendors pushed, or closes
   *  it when Vendors is already showing. */
  programVendorsRequested: boolean;
  /** True while ProgramVendorsView is mounted, so the rail can light up. */
  programVendorsShown: boolean;
  toggleProgramVendors: () => void;
  clearProgramVendorsRequest: () => void;
  setProgramVendorsShown: (shown: boolean) => void;
  /** One-shot: a Program panel next move asks the plan editor to open one
   *  of its companions (Assign, events, a party, updates) on `planId` once
   *  that plan is the active own plan. DefensePlanPanel.web.tsx consumes and
   *  clears it; an id it can't find is dropped, like pendingPlanAssignFocus. */
  pendingPlanCompanion: { planId: string; companion: PlanCompanion } | null;
  requestPlanCompanion: (planId: string, companion: PlanCompanion) => void;
  clearPlanCompanion: () => void;

  // --- Map key visibility (desktop web, Sept 2026 A2) ---------------------
  // Session-only, reset on sign-out. null (the default) means "no explicit
  // choice yet" — selectMapKeyShown below then follows planPanelOpen, the
  // same "collapsed while editing" default the key always had. Once the
  // rail's Key row is pressed, the explicit true/false wins regardless of
  // planPanelOpen, until sign-out clears it back to null.
  mapKeyOverride: boolean | null;
  toggleMapKey: () => void;

  // --- Defense plan drawing (desktop web, Sept 2026) ----------------------
  // Session-only, reset on sign-out like the overlay toggles below.
  /** The plan editor is docked beside the map. Closing it drops the tool. */
  planPanelOpen: boolean;
  openPlanPanel: () => void;
  closePlanPanel: () => void;
  /** Which strategy and shape the next map click draws; null when the user
   *  is not drawing, which is the only state in which map clicks reach the
   *  markers again. `kind: 'home'` picks a house rather than drawing a
   *  shape — see PlanDrawKind's doc comment. */
  planDrawTool: { strategy: DefenseStrategy; kind: PlanDrawKind } | null;
  setPlanDrawTool: (tool: { strategy: DefenseStrategy; kind: PlanDrawKind } | null) => void;
  /** True while a line or area has at least one vertex placed. The plan
   *  keyboard shortcuts read it: digits may re-pick a strategy while a tool
   *  is armed but idle, never mid-stroke. Set by useDefensePlanDraw.web.ts. */
  planStrokeActive: boolean;
  setPlanStrokeActive: (active: boolean) => void;
  /** One-shot counter: useDefensePlanDraw.web.ts bumps this when a
   *  home-mode map click misses every loaded structure within
   *  NEAREST_STRUCTURE_MAX_METERS (planHomes.ts), so the panel can flash a
   *  brief "no home there" hint without blocking on an alert(). A counter,
   *  not a boolean, so a second miss in a row still re-fires the panel's
   *  own dismiss timer — same shape as slideshowOpenRequestId. */
  planHomeMissId: number;
  flagPlanHomeMiss: () => void;
  /** Bumped when a finish gesture finds a stroke with too few points; the
   *  panel flashes "Draw a little longer". Counter for the same reason as
   *  planHomeMissId. */
  planTooShortId: number;
  flagPlanTooShort: () => void;
  /** The plan the map is rendering: the user's own while editing, or one
   *  opened from the area list or a share link. The document itself lives
   *  in defensePlansStore.viewedPlan; this is the id it subscribes to. */
  viewedPlanId: string | null;
  setViewedPlanId: (planId: string | null) => void;
  /** Feature row highlighted in the panel after a click on its line. */
  selectedPlanFeatureId: string | null;
  setSelectedPlanFeature: (featureId: string | null) => void;
  /** Rows or shapes picked together with Cmd/Ctrl/Shift+click, in pick
   *  order. selectedPlanFeatureId stays the anchor (the last one picked)
   *  and is always a member while the set is non-empty. A plain click
   *  clears the set. The drag-to-bundle work reads this slice. */
  planMultiSelection: string[];
  togglePlanMultiSelection: (featureId: string) => void;
  /** Shift+click: selects the run between the anchor and `featureId`
   *  over `visibleIds` (the rows in on-screen order). No anchor, or an
   *  anchor that is not visible, falls back to a plain single select. */
  rangePlanMultiSelection: (featureId: string, visibleIds: string[]) => void;
  setPlanMultiSelection: (ids: string[]) => void;
  clearPlanMultiSelection: () => void;
  /** Bundle whose header opens straight into its rename field (set right
   *  after "Bundle" on a selection). The header clears it once it reads it. */
  planBundleRenamingId: string | null;
  setPlanBundleRenamingId: (bundleId: string | null) => void;
  /** The last "Copy as another defense": the row copied from and the new
   *  one. "Make a bundle" seeds its picker with the pair when the copy is
   *  still the selected row. Cleared on a plan switch and on reset. */
  lastPlanCopy: { sourceId: string; copyId: string } | null;
  setLastPlanCopy: (copy: { sourceId: string; copyId: string } | null) => void;
  /** Feature whose "Copy as" menu is open. Lives here, not in the row, so
   *  the C key can open the selected row's menu. */
  planCopyMenuFeatureId: string | null;
  setPlanCopyMenuFeature: (featureId: string | null) => void;
  /** Feature whose ⋯ row menu is open (right click, Shift+F10 and the
   *  ContextMenu key open the selected row's). */
  planRowMenuFeatureId: string | null;
  setPlanRowMenuFeature: (featureId: string | null) => void;
  /** Right-click menu on a defense shape on the map: which feature, and
   *  the cursor's viewport position. Closed by a picker, an armed draw
   *  tool, a list drag, a map pan or zoom, and by opening the row menus. */
  planFeatureContextMenu: { featureId: string; x: number; y: number } | null;
  openPlanFeatureContextMenu: (menu: { featureId: string; x: number; y: number }) => void;
  closePlanFeatureContextMenu: () => void;
  /** Feature the pointer is over, on either side: a list row or its shape
   *  on the map. Each side lights the other. Lighter than selection. */
  hoveredPlanFeatureId: string | null;
  setHoveredPlanFeature: (featureId: string | null) => void;
  /** Clears the hover only when `featureId` still holds it, so a row's
   *  pointer-leave can't wipe the hover the next row just set. */
  clearHoveredPlanFeature: (featureId: string) => void;
  /** A defense row being dragged in the plan list (usePlanListDrag.web.ts).
   *  `target` and `outcome` follow the pointer; the bundle cards, loose rows
   *  and the drag ghost read them. Null when nothing is being dragged. */
  planListDrag: {
    featureIds: string[];
    sourceBundleId: string | null;
    target: PlanDropTarget | null;
    outcome: PlanDropOutcome | null;
  } | null;
  setPlanListDrag: (drag: UIState['planListDrag']) => void;
  /** Updates only when the target (or what a drop there would do) changes. */
  setPlanListDragTarget: (target: PlanDropTarget | null, outcome: PlanDropOutcome | null) => void;
  /** Bundle whose header the pointer is over; the map lights all members. */
  hoveredPlanBundleId: string | null;
  setHoveredPlanBundle: (bundleId: string | null) => void;
  clearHoveredPlanBundle: (bundleId: string) => void;
  /** One-shot: the panel's feature row asks the map to fit to one feature's
   *  bounds without changing what's rendered — same reasoning as
   *  pendingMapFocus, scoped to a feature already known to be part of the
   *  plan the map is currently showing, so no plan lookup is needed.
   *  useDefensePlanLayer.web.ts consumes + clears it. */
  pendingPlanFeatureFocus: string | null;
  requestPlanFeatureFocus: (featureId: string) => void;
  clearPlanFeatureFocus: () => void;

  // --- Bundle selection (Sept 16 2026 — "bundle steps") -------------------
  /** Non-null while the panel's Bundle picker is open — the set of feature
   *  ids currently checked. Null means the picker is closed (its usual
   *  state). Cleared whenever a draw tool is armed (setPlanDrawTool below)
   *  or the panel closes (closePlanPanel), same as planDrawTool itself. */
  planBundleSelection: string[] | null;
  /** Non-null while the picker is editing that bundle's members (the
   *  header menu's "Add or remove defenses") rather than making a new one.
   *  Only ever set together with planBundleSelection and cleared with it. */
  planBundleEditingId: string | null;
  /** Set for 1.2s after an assignment save lands on a defense; that row shows
   *  a check and fades its new "who" text in. Local feedback, not a toast. */
  justAssignedFeature: { id: string; at: number } | null;
  flagJustAssigned: (featureId: string) => void;
  /** Same for a bundle header after "Assign" on the whole bundle saves. */
  justAssignedBundle: { id: string; at: number } | null;
  flagJustAssignedBundle: (bundleId: string) => void;
  /** `seedIds` (non-empty) opens the picker with exactly those ticked;
   *  otherwise it seeds from lastPlanCopy as before. */
  startPlanBundleSelection: (seedIds?: string[]) => void;
  /** Replaces the ticked set (Select all / Clear). No-op unless the picker is open. */
  setPlanBundleSelection: (ids: string[]) => void;
  /** Opens the picker on an existing bundle, its current members pre-checked. */
  startPlanBundleEdit: (bundleId: string, memberIds: string[]) => void;
  togglePlanBundleSelectionMember: (featureId: string) => void;
  cancelPlanBundleSelection: () => void;
  /** A whole bundle picked by clicking its header row: every member
   *  highlights on the map (useDefensePlanLayer.web.ts resolves this id into
   *  its member set) instead of the one shape selectedPlanFeatureId picks.
   *  The two are mutually exclusive — each setter clears the other. */
  selectedPlanBundleId: string | null;
  setSelectedPlanBundle: (bundleId: string | null) => void;
  /** One-shot, same shape as pendingPlanFeatureFocus: a bundle header's
   *  click asks the map to fit to every member's combined bounds. */
  pendingPlanBundleFocus: string | null;
  requestPlanBundleFocus: (bundleId: string) => void;
  clearPlanBundleFocus: () => void;
  /** Bumped by "Zoom to plan" in the editor header menu; Map.web.tsx fits
   *  the owner's whole plan when it changes. A counter, not a one-shot
   *  request, so asking twice in a row fits twice. */
  planFitNonce: number;
  requestPlanFit: () => void;
  /** One-shot, same shape and reasoning as slideshowOpenRequestId: the
   *  Layers sheet is mounted globally and cannot reach the map, so it asks
   *  and Map.web.tsx consumes + clears. */
  planPanelOpenRequestId: number;
  requestPlanPanelOpen: () => void;
  clearPlanPanelOpenRequest: () => void;
  /** One-shot: a `?plan=<id>&assign=<featureId>` deep link (panel-system.md
   *  "Deep links on load", Phase 4A) asks the plan editor to open Assign for
   *  a feature once its own plan data has loaded — usePanelDeepLinks.ts sets
   *  this, DefensePlanPanel.web.tsx consumes + clears it once the feature is
   *  actually found on `ownPlan` (a stale or wrong id is just dropped, same
   *  as every other one-shot focus channel above). */
  pendingPlanAssignFocus: { featureId: string } | null;
  requestPlanAssignFocus: (featureId: string) => void;
  clearPlanAssignFocus: () => void;

  // One-shot flag: set true by post/create after a first anonymous-author
  // post lands. Map reads it on focus to show SaveAccountToast, then clears.
  showSaveAccountToast: boolean;
  flagShowSaveAccountToast: () => void;
  clearSaveAccountToast: () => void;
  // Persisted across launches — once the user dismisses the save-account
  // nudge, the avatar dot + toast stay quiet until they sign out.
  hasDismissedSaveNudge: boolean;
  hydrateSaveNudgeDismissal: () => Promise<void>;
  dismissSaveNudge: () => void;
  resetSaveNudgeDismissal: () => void;

  // Persisted device-wide. Drives the pulsing dot on the avatar. Set true
  // when the user opens the Tour REDI sheet, or — as a migration backstop —
  // when an existing user with hasCompletedOnboarding:true first hydrates
  // after this lands. Anonymous users have no Firestore doc fields, so
  // this is local-only by design.
  hasSeenTour: boolean;
  hydrateTourSeen: () => Promise<void>;
  markTourSeen: () => void;

  // Persisted — one-time dismiss for the "pick more roles" nudge shown on the
  // profile screen to Fire Scouts with posts. Null until hydrated.
  hasDismissedRolesNudge: boolean | null;
  hydrateRolesNudge: () => Promise<void>;
  dismissRolesNudge: () => void;

  // Persisted device-wide, like the tour flag: which basemap the map screen
  // draws. A display preference, not session state, so it survives sign-out.
  // Defaults to 'standard' and stays there until hydration says otherwise —
  // a returning satellite user sees one frame of the illustrated map, which
  // beats every standard user seeing a flash of imagery.
  mapStyle: MapStyle;
  hydrateMapStyle: () => Promise<void>;
  // A setter, not a toggle: the map layers sheet picks a basemap from a
  // radio group, where re-picking the selected row must do nothing.
  setMapStyle: (style: MapStyle) => void;

  // Session-only: draws the CAL FIRE Fire Hazard Severity Zone polygons over
  // the map (web only). Unlike mapStyle this is not a device-wide display
  // preference — it is a "show me this one thing right now" toggle, so it
  // resets on sign-out same as the other session flags below.
  fhszOverlayEnabled: boolean;
  toggleFhszOverlay: () => void;

  // Session-only, same reasoning: CAL FIRE historical fire perimeters drawn
  // as outlines over the map (web only).
  fireHistoryEnabled: boolean;
  toggleFireHistory: () => void;

  // Session-only, same reasoning again: the two community program point
  // layers (web only). Firewise USA is national and the fire safe councils
  // are statewide, so unlike the hazard layers neither is scoped to a
  // service area.
  firewiseEnabled: boolean;
  toggleFirewise: () => void;
  fireSafeCouncilsEnabled: boolean;
  toggleFireSafeCouncils: () => void;

  // Session-only: which shared plan-map overlays (mapOverlaysStore) are
  // drawn on the map right now — same "show me this one thing right now"
  // reasoning as fhszOverlayEnabled, but per-overlay-id since there can be
  // several in one area. Absence from the array means off; nothing is ever
  // stored as `false`. Reset on sign-out like the other session flags here.
  enabledOverlayIds: string[];
  toggleOverlay: (id: string) => void;
  setOverlayEnabled: (id: string, on: boolean) => void;

  // Session-only: the id of the overlay currently in the corner-placement
  // editor (moderator only), or null when the editor is closed. Non-null is
  // what opens the editor — set by startOverlayPlacement (the import flow's
  // handoff, or "Edit placement" on an existing overlay), cleared by
  // stopOverlayPlacement (Cancel/Save) and by reset() on sign-out.
  placingOverlayId: string | null;
  // True when the editor should drop the image into the current map view
  // (a fresh import); false when it opens on the saved corners.
  placingOverlayFitToView: boolean;
  startOverlayPlacement: (id: string, opts?: { fitToView?: boolean }) => void;
  stopOverlayPlacement: () => void;

  // Session-only: are observation markers/clusters drawn on the map? Client
  // ask (Sept 2026): "treat these observations as a layer that could be
  // removed... when you're doing the planning." Two flags, not one, because
  // "on generally" and "on while a plan is open" are answered separately —
  // selectObservationsVisible below picks whichever is live off the same
  // planFocus condition useDefensePlanLayer.web.ts uses for PLAN_FOCUS_CLASS
  // (planPanelOpen || viewedPlanId != null). Reset on sign-out like
  // fhszOverlayEnabled/fireHistoryEnabled above. Both default on since Oct 8
  // 2026 (Glenn has the room to work now): while planning they show faded
  // by PLAN_FOCUS_CLASS and take no clicks while a draw tool is armed.
  observationsLayerEnabled: boolean;
  observationsWhilePlanning: boolean;
  /** Writes whichever flag is live for the current mode, so callers (the
   *  Layers sheet's Observations row) don't need to know which one that is. */
  setObservationsVisible: (visible: boolean) => void;

  // Persisted — one-time coachmark pointing at the floating "+" button.
  // Null until hydrated so the coachmark doesn't flash then hide.
  hasSeenCreateCoachmark: boolean | null;
  hydrateCreateCoachmark: () => Promise<void>;
  markCreateCoachmarkSeen: () => void;

  // Persisted — one-time guidance sheet shown before the observation
  // composer (client ask, Sept 2026: "guidance on what to look for and
  // why"). Null until hydrated, same reasoning as hasSeenCreateCoachmark.
  // See useObservationGuidance for the gate that reads this.
  hasSeenObservationGuidance: boolean | null;
  hydrateObservationGuidance: () => Promise<void>;
  markObservationGuidanceSeen: () => void;
  // Session-only: the composer path to resume once the guidance sheet's
  // Continue is pressed. Set by useObservationGuidance.guideThen before it
  // opens the sheet; null when the sheet is opened informationally (the
  // composer's own "What should I look for?" link, the tour's row) so
  // Continue just closes without navigating. Not persisted; cleared on
  // sign-out by reset() like the other session-only sheet/flow state above.
  observationGuidanceNext: string | null;
  setObservationGuidanceNext: (path: string | null) => void;

  // Persisted device-wide. Drives MapPathsOverlay, the first-visit call to
  // action over the map (three cards: evaluate a property, post an
  // observation, post news) — shown every visit until the user engages a
  // card or dismisses it, then never again. Null until hydrated, same
  // reasoning as hasSeenCreateCoachmark: with a `false` default the overlay
  // would flash in before the async AsyncStorage read resolves for
  // returning users. Survives sign-out on purpose — not touched in reset().
  hasSeenMapPaths: boolean | null;
  hydrateMapPaths: () => Promise<void>;
  markMapPathsEngaged: () => void;

  // Session-only re-open signal for MapPathsOverlay: the Guidance tab is the
  // overlay's only on-demand entry point now (the old sheet is retired), so
  // pressing it re-summons the overlay even after hasSeenMapPaths is true.
  // Not persisted, not touched by reset() — a mid-session tab tap isn't
  // account or device state. MapPathsOverlay clears it the moment a card is
  // tapped or it's dismissed (see markMapPathsEngaged call sites).
  mapPathsOverlayRequested: boolean;
  requestMapPathsOverlay: () => void;
  clearMapPathsOverlayRequest: () => void;

  // Session-only: true for the one visit a `/plan/<id>` share link routed
  // in. A first-time visitor's reason for being here is the plan someone
  // sent them, not REDI itself, so MapPathsOverlay skips its automatic
  // first-visit show for this one visit without marking hasSeenMapPaths — a
  // normal visit later still gets it. Folded into selectMapPathsOverlayActive
  // so the sign-in chip and the create coachmark stand aside for the plan
  // viewer the same way they would for the welcome overlay (see that
  // selector's comment). Not persisted; reset() clears it like the other
  // session-only flags above.
  arrivedViaPlanLink: boolean;
  markArrivedViaPlanLink: () => void;

  // Session-only one-shot: the Property tab (and MapPathsOverlay's Property
  // card) asks the map screen to focus its address search bar when the user
  // has no claimed structure to jump to. The search bar ref lives in
  // app/(tabs)/index.tsx, and the tab can be pressed from any screen, so the
  // request travels through the store the same way mapPathsOverlayRequested
  // does. Not persisted, not touched by reset().
  mapSearchFocusRequested: boolean;
  requestMapSearchFocus: () => void;
  clearMapSearchFocusRequest: () => void;

  // Insurer-link journey (see the type docs above). Session-scoped and
  // sessionStorage-mirrored on web; deliberately NOT touched by reset() —
  // the try-then-save round trip crosses an auth boundary, and a mid-journey
  // sign-out/sign-in must not strand the visitor's link or ticks.
  pendingProperty: PendingProperty | null;
  // The model an insurer entry page (/start/fair-plan) chose, before the
  // visitor has picked a home. Same treatment as pendingProperty; cleared
  // by clearPropertyJourney once a claim lands.
  pendingModel: string | null;
  setPendingModel: (checklistId: string) => void;
  setPendingProperty: (pending: PendingProperty) => void;
  previewEvaluation: PreviewEvaluation | null;
  setPreviewEvaluation: (preview: PreviewEvaluation) => void;
  /** Drop both journey objects where they reference this structure — called
   *  once a claim for it lands (the real data now owns the state). */
  clearPropertyJourney: (structureId: string) => void;

  // The audience whose intro page the visitor arrived on (/start/<audience>).
  // Same session-scoped, sessionStorage-mirrored, not-cleared-by-reset()
  // treatment as pendingProperty above, and for the same reason: it has to
  // survive the sign-up round trip, which on web can include a full reload.
  // Read by the sign-up form and the upgrade sheet to pre-tick that role.
  pendingAudience: UserRole | null;
  setPendingAudience: (audience: UserRole) => void;
  clearPendingAudience: () => void;

  // Session-only flag: a new service-worker version has installed and is
  // waiting. Drives the passive update dot on the profile avatar (Header on
  // mobile, SidebarNav's account card on the desktop shell) and the profile
  // footer's tap-to-reload pill. Web-only in practice (registerServiceWorker
  // is a no-op on native). Not reset on sign-out — an available update is
  // independent of the auth session.
  swUpdateAvailable: boolean;
  flagUpdateAvailable: () => void;

  // Session-only: is the map currently drawing individual property markers?
  // Both platforms hide them at wide zoom (see PIN_RENDER_MAX_DELTA and
  // STRUCTURE_RENDER_MIN_ZOOM_WEB), and nothing on screen said so — the map
  // key reads this to show "Zoom in for properties". Written by
  // the two render gates themselves, not derived from the region, so the line
  // can't disagree with what the map actually drew. Not part of reset(): it
  // describes the viewport, not the session.
  propertyPinsVisible: boolean;
  setPropertyPinsVisible: (visible: boolean) => void;

  // Session-only history for the Search tab's keyboard-first path. Newest
  // first, deduped by id, capped — not persisted (a fresh launch starts
  // empty by design). Recorded on structure-detail mount and search select.
  recentlyViewedStructures: Structure[];
  recordStructureView: (structure: Structure) => void;

  // Session teardown, called by authStore.signOut. Resets sheet/modal-derived
  // state and the save-account nudge; device-wide flags (tour, roles nudge,
  // coachmark, map paths, basemap style) survive sign-out on purpose.
  reset: () => void;
}

/** How long a row shows its just-assigned check. */
export const JUST_ASSIGNED_MS = 1200;

export const useUIStore = create<UIState>((set, get) => ({
  tabBarVisible: true,
  _hideCount: 0,
  showTabBar: () =>
    set((s) => {
      const next = Math.max(0, s._hideCount - 1);
      return { _hideCount: next, tabBarVisible: next === 0 };
    }),
  hideTabBar: () => set((s) => ({ _hideCount: s._hideCount + 1, tabBarVisible: false })),

  activeSheet: null,
  sheetOpenTick: 0,
  addPropertyInitialQuery: '',
  userMenuAnchor: 'header',
  openSheet: (sheet, opts) =>
    set((s) => ({
      activeSheet: sheet,
      sheetOpenTick: s.sheetOpenTick + 1,
      addPropertyInitialQuery: sheet === 'addProperty' ? (opts?.addPropertyInitialQuery ?? '') : '',
      userMenuAnchor: sheet === 'userMenu' ? (opts?.userMenuAnchor ?? 'header') : s.userMenuAnchor,
    })),
  closeSheet: () => set({ activeSheet: null, addPropertyInitialQuery: '' }),

  toast: null,
  showToast: (opts) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    get().toast?.onClose?.();
    set({
      toast: {
        id,
        message: opts.message,
        action: opts.action,
        durationMs: opts.durationMs ?? (opts.action ? 6000 : 4000),
        onClose: opts.onClose,
      },
    });
    return id;
  },
  dismissToast: (id) => {
    const current = get().toast;
    if (current?.id !== id) return;
    set({ toast: null });
    current.onClose?.();
  },

  pendingMapFocus: null,
  requestMapFocus: (structureId) =>
    set({ pendingMapFocus: { kind: 'structure', id: structureId } }),
  requestMapPostFocus: (postId) => set({ pendingMapFocus: { kind: 'post', id: postId } }),
  requestMapPlanFocus: (planId) => set({ pendingMapFocus: { kind: 'plan', id: planId } }),
  clearMapFocus: () => set({ pendingMapFocus: null }),

  slideshowOpenRequestId: 0,
  requestSlideshowOpen: () =>
    set((s) => ({ slideshowOpenRequestId: s.slideshowOpenRequestId + 1 })),
  clearSlideshowOpenRequest: () => set({ slideshowOpenRequestId: 0 }),
  slideshowCloseRequestId: 0,
  requestSlideshowClose: () =>
    set((s) => ({ slideshowCloseRequestId: s.slideshowCloseRequestId + 1 })),
  clearSlideshowCloseRequest: () => set({ slideshowCloseRequestId: 0 }),
  presentationActive: false,
  setPresentationActive: (active) => set({ presentationActive: active }),

  dockedPostId: null,
  openDockedPost: (postId) => {
    set({ dockedPostId: postId });
    get().setPanelNewest('right');
  },
  closeDockedPost: () => set({ dockedPostId: null }),

  composerDockOpen: false,
  openComposerDock: () => {
    set({ composerDockOpen: true });
    get().setPanelNewest('right');
  },
  closeComposerDock: () => set({ composerDockOpen: false, composerPin: null }),
  composerPin: null,
  setComposerPin: (pin) => set({ composerPin: pin }),

  mapTopInset: 0,
  setMapTopInset: (px) => set({ mapTopInset: px }),
  dockInsets: {},
  registerDockInset: (id, width, offset = 0) =>
    set((s) => ({ dockInsets: { ...s.dockInsets, [id]: { width, offset } } })),
  unregisterDockInset: (id) =>
    set((s) => {
      if (!(id in s.dockInsets)) return s;
      const next = { ...s.dockInsets };
      delete next[id];
      return { dockInsets: next };
    }),
  leftDockInsets: {},
  registerLeftDockInset: (id, width, offset = 0) =>
    set((s) => ({ leftDockInsets: { ...s.leftDockInsets, [id]: { width, offset } } })),
  unregisterLeftDockInset: (id) =>
    set((s) => {
      if (!(id in s.leftDockInsets)) return s;
      const next = { ...s.leftDockInsets };
      delete next[id];
      return { leftDockInsets: next };
    }),

  panelNewest: 'left',
  setPanelNewest: (side) => set((s) => (s.panelNewest === side ? s : { panelNewest: side })),

  layersPanelOpen: false,
  toggleLayersPanel: () =>
    set((s) => {
      const opening = !s.layersPanelOpen;
      return {
        layersPanelOpen: opening,
        // One left panel at a time between the browse panels.
        peoplePanelOpen: opening ? false : s.peoplePanelOpen,
        programPanelOpen: opening ? false : s.programPanelOpen,
        // Layers paints over the plan editor rather than closing it (mount
        // order in app/(tabs)/index.tsx decides who's on top) — an armed
        // draw tool must not go on drawing under a hidden palette while
        // Layers covers it, so disarm it the same way closePlanPanel does.
        // The editor stays mounted; this undoes only the arming, not
        // ownPlan or any other editor state, and it returns when Layers
        // closes.
        planDrawTool: opening ? null : s.planDrawTool,
      };
    }),
  closeLayersPanel: () => set({ layersPanelOpen: false }),

  peoplePanelOpen: false,
  togglePeoplePanel: () =>
    set((s) => {
      const opening = !s.peoplePanelOpen;
      return {
        peoplePanelOpen: opening,
        layersPanelOpen: opening ? false : s.layersPanelOpen,
        programPanelOpen: opening ? false : s.programPanelOpen,
        // Same reason as toggleLayersPanel: People paints over the plan
        // editor, so an armed draw tool must not keep drawing under it.
        planDrawTool: opening ? null : s.planDrawTool,
      };
    }),
  closePeoplePanel: () => set({ peoplePanelOpen: false }),

  programPanelOpen: false,
  toggleProgramPanel: () =>
    set((s) => {
      const opening = !s.programPanelOpen;
      return {
        programPanelOpen: opening,
        layersPanelOpen: opening ? false : s.layersPanelOpen,
        peoplePanelOpen: opening ? false : s.peoplePanelOpen,
        // Same reason as toggleLayersPanel: it paints over the plan editor.
        planDrawTool: opening ? null : s.planDrawTool,
      };
    }),
  closeProgramPanel: () => set({ programPanelOpen: false }),
  programVendorsRequested: false,
  programVendorsShown: false,
  toggleProgramVendors: () =>
    set((s) => {
      if (s.programPanelOpen && s.programVendorsShown) return { programPanelOpen: false };
      return {
        programPanelOpen: true,
        programVendorsRequested: true,
        layersPanelOpen: false,
        peoplePanelOpen: false,
        planDrawTool: null,
      };
    }),
  clearProgramVendorsRequest: () => set({ programVendorsRequested: false }),
  setProgramVendorsShown: (shown) => set({ programVendorsShown: shown }),
  pendingPlanCompanion: null,
  requestPlanCompanion: (planId, companion) => set({ pendingPlanCompanion: { planId, companion } }),
  clearPlanCompanion: () => set({ pendingPlanCompanion: null }),

  mapKeyOverride: null,
  // Flips the tri-state the short way: from the follow-the-default state
  // (null), the first press picks the opposite of whatever is showing right
  // now; after that it's a plain true/false toggle. Reading the current
  // shown-ness through the selector (not just `mapKeyOverride`) is what
  // makes the very first press always do the thing the row's icon implies —
  // otherwise a fresh `null -> true` on a key already showing by default
  // would be a no-op tap.
  toggleMapKey: () => set((s) => ({ mapKeyOverride: !selectMapKeyShown(s) })),

  planPanelOpen: false,
  // Mutually exclusive with the plan-map placement editor below (both are
  // left-docked, and drawing a defense plan or dragging a plan-map image
  // are both mouse gestures on the same map — only one can own it at a
  // time). Each opener closes the other's surface first. Also closes the
  // layers panel: opening the editor is a deliberate switch to planning, so
  // a Layers panel left open from browsing shouldn't linger over it. Layers
  // can still be reopened on top afterward (toggleLayersPanel above doesn't
  // close the editor back) — this only covers entering planning, not being
  // in it.
  openPlanPanel: () => {
    get().stopOverlayPlacement();
    set({
      planPanelOpen: true,
      layersPanelOpen: false,
      peoplePanelOpen: false,
      programPanelOpen: false,
    });
  },
  closePlanPanel: () => {
    // The editor's meta line shows saveError until the next write — clear
    // it on close too, so a failed write from a previous session doesn't
    // reappear the next time the panel opens.
    useDefensePlansStore.getState().clearSaveError();
    set({
      planPanelOpen: false,
      planDrawTool: null,
      selectedPlanFeatureId: null,
      planMultiSelection: [],
      planBundleRenamingId: null,
      lastPlanCopy: null,
      planCopyMenuFeatureId: null,
      planRowMenuFeatureId: null,
      planFeatureContextMenu: null,
      hoveredPlanFeatureId: null,
      hoveredPlanBundleId: null,
      planListDrag: null,
      planBundleSelection: null,
      planBundleEditingId: null,
      selectedPlanBundleId: null,
    });
  },
  justAssignedFeature: null,
  flagJustAssigned: (featureId) => {
    const at = Date.now();
    set({ justAssignedFeature: { id: featureId, at } });
    setTimeout(() => {
      if (get().justAssignedFeature?.at === at) set({ justAssignedFeature: null });
    }, JUST_ASSIGNED_MS);
  },
  justAssignedBundle: null,
  flagJustAssignedBundle: (bundleId) => {
    const at = Date.now();
    set({ justAssignedBundle: { id: bundleId, at } });
    setTimeout(() => {
      if (get().justAssignedBundle?.at === at) set({ justAssignedBundle: null });
    }, JUST_ASSIGNED_MS);
  },
  planDrawTool: null,
  // Arming a draw tool exits the bundle picker (drawing and picking are
  // mutually exclusive things to be doing with the map); disarming one
  // (tool: null, e.g. Escape) leaves whatever bundle-selection state was
  // already there alone.
  setPlanDrawTool: (tool) =>
    set((s) => ({
      planDrawTool: tool,
      planFeatureContextMenu: tool ? null : s.planFeatureContextMenu,
      planListDrag: tool ? null : s.planListDrag,
      planBundleSelection: tool ? null : s.planBundleSelection,
      planBundleEditingId: tool ? null : s.planBundleEditingId,
      planMultiSelection: tool ? [] : s.planMultiSelection,
    })),
  planStrokeActive: false,
  setPlanStrokeActive: (active) =>
    set((s) => (s.planStrokeActive === active ? s : { planStrokeActive: active })),
  planHomeMissId: 0,
  planTooShortId: 0,
  flagPlanHomeMiss: () => set((s) => ({ planHomeMissId: s.planHomeMissId + 1 })),
  flagPlanTooShort: () => set((s) => ({ planTooShortId: s.planTooShortId + 1 })),
  viewedPlanId: null,
  // Closes the layers panel only when a plan is being shown (planId !==
  // null), not when the viewer is dismissed — clearing viewedPlanId isn't a
  // reason to also close a Layers panel the user may have opened since, or
  // may be mid-interaction with (see uiStore.test.ts: "does not close").
  setViewedPlanId: (planId) =>
    set((s) => ({
      viewedPlanId: planId,
      selectedPlanFeatureId: null,
      planMultiSelection: [],
      hoveredPlanFeatureId: null,
      hoveredPlanBundleId: null,
      planListDrag: null,
      selectedPlanBundleId: null,
      layersPanelOpen: planId !== null ? false : s.layersPanelOpen,
      peoplePanelOpen: planId !== null ? false : s.peoplePanelOpen,
      programPanelOpen: planId !== null ? false : s.programPanelOpen,
    })),
  selectedPlanFeatureId: null,
  setSelectedPlanFeature: (featureId) =>
    set({ selectedPlanFeatureId: featureId, selectedPlanBundleId: null, planMultiSelection: [] }),
  planMultiSelection: [],
  togglePlanMultiSelection: (featureId) =>
    set((s) => {
      // A first Cmd+click with a lone anchor selected starts the set from
      // that anchor, so the pair reads as "2 selected".
      const base =
        s.planMultiSelection.length === 0 && s.selectedPlanFeatureId
          ? [s.selectedPlanFeatureId]
          : s.planMultiSelection;
      if (base.includes(featureId)) {
        const next = base.filter((id) => id !== featureId);
        const anchor =
          s.selectedPlanFeatureId === featureId
            ? (next[next.length - 1] ?? null)
            : s.selectedPlanFeatureId;
        return {
          planMultiSelection: next,
          selectedPlanFeatureId: anchor,
          selectedPlanBundleId: null,
        };
      }
      return {
        planMultiSelection: [...base, featureId],
        selectedPlanFeatureId: featureId,
        selectedPlanBundleId: null,
      };
    }),
  rangePlanMultiSelection: (featureId, visibleIds) =>
    set((s) => {
      const from = s.selectedPlanFeatureId ? visibleIds.indexOf(s.selectedPlanFeatureId) : -1;
      const to = visibleIds.indexOf(featureId);
      if (from < 0 || to < 0) {
        return {
          selectedPlanFeatureId: featureId,
          selectedPlanBundleId: null,
          planMultiSelection: [],
        };
      }
      const [lo, hi] = from <= to ? [from, to] : [to, from];
      const run = visibleIds.slice(lo, hi + 1);
      // Anchor stays where it was: a second Shift+click re-ranges from it.
      const anchor = s.selectedPlanFeatureId as string;
      return { planMultiSelection: run, selectedPlanFeatureId: anchor, selectedPlanBundleId: null };
    }),
  setPlanMultiSelection: (ids) =>
    set((s) => {
      if (ids.length === 0) return { planMultiSelection: [] };
      const anchor =
        s.selectedPlanFeatureId && ids.includes(s.selectedPlanFeatureId)
          ? s.selectedPlanFeatureId
          : ids[0];
      return {
        planMultiSelection: [...ids],
        selectedPlanFeatureId: anchor,
        selectedPlanBundleId: null,
      };
    }),
  clearPlanMultiSelection: () =>
    set((s) => (s.planMultiSelection.length ? { planMultiSelection: [] } : s)),
  planBundleRenamingId: null,
  setPlanBundleRenamingId: (bundleId) => set({ planBundleRenamingId: bundleId }),
  lastPlanCopy: null,
  setLastPlanCopy: (copy) => set({ lastPlanCopy: copy }),
  planCopyMenuFeatureId: null,
  setPlanCopyMenuFeature: (featureId) =>
    set((s) => ({
      planCopyMenuFeatureId: featureId,
      planFeatureContextMenu: featureId ? null : s.planFeatureContextMenu,
    })),
  planRowMenuFeatureId: null,
  setPlanRowMenuFeature: (featureId) =>
    set((s) => ({
      planRowMenuFeatureId: featureId,
      planFeatureContextMenu: featureId ? null : s.planFeatureContextMenu,
    })),
  planFeatureContextMenu: null,
  openPlanFeatureContextMenu: (menu) =>
    set({
      planFeatureContextMenu: menu,
      planRowMenuFeatureId: null,
      planCopyMenuFeatureId: null,
    }),
  closePlanFeatureContextMenu: () =>
    set((s) => (s.planFeatureContextMenu ? { planFeatureContextMenu: null } : s)),
  hoveredPlanFeatureId: null,
  setHoveredPlanFeature: (featureId) =>
    set((s) => (s.hoveredPlanFeatureId === featureId ? s : { hoveredPlanFeatureId: featureId })),
  clearHoveredPlanFeature: (featureId) =>
    set((s) => (s.hoveredPlanFeatureId === featureId ? { hoveredPlanFeatureId: null } : s)),
  planListDrag: null,
  setPlanListDrag: (drag) =>
    set((s) => ({
      planListDrag: drag,
      planFeatureContextMenu: drag ? null : s.planFeatureContextMenu,
    })),
  setPlanListDragTarget: (target, outcome) =>
    set((s) => {
      const cur = s.planListDrag;
      if (!cur) return s;
      const same =
        cur.target?.type === target?.type &&
        (cur.target as { id?: string } | null)?.id === (target as { id?: string } | null)?.id &&
        cur.outcome?.kind === outcome?.kind &&
        cur.outcome?.label === outcome?.label &&
        cur.outcome?.disabled === outcome?.disabled;
      return same ? s : { planListDrag: { ...cur, target, outcome } };
    }),
  hoveredPlanBundleId: null,
  setHoveredPlanBundle: (bundleId) =>
    set((s) => (s.hoveredPlanBundleId === bundleId ? s : { hoveredPlanBundleId: bundleId })),
  clearHoveredPlanBundle: (bundleId) =>
    set((s) => (s.hoveredPlanBundleId === bundleId ? { hoveredPlanBundleId: null } : s)),
  pendingPlanFeatureFocus: null,
  requestPlanFeatureFocus: (featureId) => set({ pendingPlanFeatureFocus: featureId }),
  clearPlanFeatureFocus: () => set({ pendingPlanFeatureFocus: null }),
  planPanelOpenRequestId: 0,
  requestPlanPanelOpen: () =>
    set((s) => ({ planPanelOpenRequestId: s.planPanelOpenRequestId + 1 })),
  clearPlanPanelOpenRequest: () => set({ planPanelOpenRequestId: 0 }),
  pendingPlanAssignFocus: null,
  requestPlanAssignFocus: (featureId) => set({ pendingPlanAssignFocus: { featureId } }),
  clearPlanAssignFocus: () => set({ pendingPlanAssignFocus: null }),

  planBundleSelection: null,
  planBundleEditingId: null,
  // Starting the picker also disarms any draw tool — the invariant is "at
  // most one of {draw tool, bundle picker} is active", the same invariant
  // setPlanDrawTool enforces from the other direction.
  // A fresh copy that is still the selected row starts the picker with the
  // pair ticked (copy a defense, then bundle it with its source). Skipped
  // when either already sits in a bundle: the copy joined its source's.
  startPlanBundleSelection: (seedIds) =>
    set((s) => {
      if (seedIds && seedIds.length > 0) {
        return {
          planBundleSelection: [...seedIds],
          planBundleEditingId: null,
          planDrawTool: null,
          planFeatureContextMenu: null,
          planListDrag: null,
          planMultiSelection: [],
        };
      }
      let seed: string[] = [];
      const copy = s.lastPlanCopy;
      if (copy && s.selectedPlanFeatureId === copy.copyId) {
        const features = useDefensePlansStore.getState().ownPlan?.features ?? [];
        const source = features.find((f) => f.id === copy.sourceId);
        const copied = features.find((f) => f.id === copy.copyId);
        if (source && copied && !source.bundleId && !copied.bundleId) {
          seed = [copy.sourceId, copy.copyId];
        }
      }
      return {
        planBundleSelection: seed,
        planBundleEditingId: null,
        planDrawTool: null,
        planFeatureContextMenu: null,
        planListDrag: null,
        planMultiSelection: [],
      };
    }),
  startPlanBundleEdit: (bundleId, memberIds) =>
    set({
      planBundleSelection: [...memberIds],
      planBundleEditingId: bundleId,
      planDrawTool: null,
      planFeatureContextMenu: null,
      planListDrag: null,
      planMultiSelection: [],
    }),
  setPlanBundleSelection: (ids) =>
    set((s) => (s.planBundleSelection ? { planBundleSelection: [...ids] } : s)),
  togglePlanBundleSelectionMember: (featureId) =>
    set((s) => {
      if (!s.planBundleSelection) return s;
      const already = s.planBundleSelection.includes(featureId);
      return {
        planBundleSelection: already
          ? s.planBundleSelection.filter((id) => id !== featureId)
          : [...s.planBundleSelection, featureId],
      };
    }),
  cancelPlanBundleSelection: () => set({ planBundleSelection: null, planBundleEditingId: null }),
  selectedPlanBundleId: null,
  setSelectedPlanBundle: (bundleId) =>
    set({ selectedPlanBundleId: bundleId, selectedPlanFeatureId: null, planMultiSelection: [] }),
  pendingPlanBundleFocus: null,
  requestPlanBundleFocus: (bundleId) => set({ pendingPlanBundleFocus: bundleId }),
  clearPlanBundleFocus: () => set({ pendingPlanBundleFocus: null }),
  planFitNonce: 0,
  requestPlanFit: () => set((s) => ({ planFitNonce: s.planFitNonce + 1 })),

  swUpdateAvailable: false,
  flagUpdateAvailable: () => set({ swUpdateAvailable: true }),

  // Starts false: every map opens at a wide default region (DEFAULT_REGION),
  // so the first honest answer is "no property pins yet".
  propertyPinsVisible: false,
  setPropertyPinsVisible: (visible) => {
    // Called from imperative map render paths on every pan and zoom. Guard so
    // a pan at unchanged zoom doesn't notify subscribers.
    if (get().propertyPinsVisible !== visible) set({ propertyPinsVisible: visible });
  },

  showSaveAccountToast: false,
  flagShowSaveAccountToast: () => set({ showSaveAccountToast: true }),
  clearSaveAccountToast: () => set({ showSaveAccountToast: false }),
  hasDismissedSaveNudge: false,
  hydrateSaveNudgeDismissal: async () => {
    try {
      const v = await AsyncStorage.getItem(SAVE_NUDGE_KEY);
      if (v === '1') set({ hasDismissedSaveNudge: true });
    } catch {
      // non-fatal — default stays false
    }
  },
  dismissSaveNudge: () => {
    set({ hasDismissedSaveNudge: true, showSaveAccountToast: false });
    AsyncStorage.setItem(SAVE_NUDGE_KEY, '1').catch(() => {});
  },
  resetSaveNudgeDismissal: () => {
    set({ hasDismissedSaveNudge: false });
    AsyncStorage.removeItem(SAVE_NUDGE_KEY).catch(() => {});
  },

  hasSeenTour: false,
  hydrateTourSeen: async () => {
    try {
      const v = await AsyncStorage.getItem(TOUR_SEEN_KEY);
      if (v === '1') set({ hasSeenTour: true });
    } catch {
      // non-fatal — default stays false
    }
  },
  markTourSeen: () => {
    set({ hasSeenTour: true });
    AsyncStorage.setItem(TOUR_SEEN_KEY, '1').catch(() => {});
  },

  hasDismissedRolesNudge: null,
  hydrateRolesNudge: async () => {
    try {
      const v = await AsyncStorage.getItem(ROLES_NUDGE_KEY);
      set({ hasDismissedRolesNudge: v === '1' });
    } catch {
      set({ hasDismissedRolesNudge: false });
    }
  },
  dismissRolesNudge: () => {
    set({ hasDismissedRolesNudge: true });
    AsyncStorage.setItem(ROLES_NUDGE_KEY, '1').catch(() => {});
  },

  mapStyle: 'standard',
  hydrateMapStyle: async () => {
    try {
      const v = await AsyncStorage.getItem(MAP_STYLE_KEY);
      if (v === 'satellite') set({ mapStyle: 'satellite' });
    } catch {
      // non-fatal — default stays 'standard'
    }
  },
  setMapStyle: (style) => {
    if (get().mapStyle === style) return;
    set({ mapStyle: style });
    AsyncStorage.setItem(MAP_STYLE_KEY, style).catch(() => {});
  },

  fhszOverlayEnabled: false,
  toggleFhszOverlay: () => set((s) => ({ fhszOverlayEnabled: !s.fhszOverlayEnabled })),

  fireHistoryEnabled: false,
  toggleFireHistory: () => set((s) => ({ fireHistoryEnabled: !s.fireHistoryEnabled })),

  firewiseEnabled: false,
  toggleFirewise: () => set((s) => ({ firewiseEnabled: !s.firewiseEnabled })),

  fireSafeCouncilsEnabled: false,
  toggleFireSafeCouncils: () =>
    set((s) => ({ fireSafeCouncilsEnabled: !s.fireSafeCouncilsEnabled })),

  enabledOverlayIds: [],
  toggleOverlay: (id) =>
    set((s) => ({
      enabledOverlayIds: s.enabledOverlayIds.includes(id)
        ? s.enabledOverlayIds.filter((x) => x !== id)
        : [...s.enabledOverlayIds, id],
    })),
  setOverlayEnabled: (id, on) =>
    set((s) => ({
      enabledOverlayIds: on
        ? s.enabledOverlayIds.includes(id)
          ? s.enabledOverlayIds
          : [...s.enabledOverlayIds, id]
        : s.enabledOverlayIds.filter((x) => x !== id),
    })),

  placingOverlayId: null,
  placingOverlayFitToView: false,
  // See openPlanPanel above: starting a placement session closes the
  // defense plan editor first, the same mutual exclusion from the other
  // direction.
  startOverlayPlacement: (id, opts) => {
    get().closePlanPanel();
    set({
      placingOverlayId: id,
      placingOverlayFitToView: opts?.fitToView ?? false,
      layersPanelOpen: false,
      peoplePanelOpen: false,
      programPanelOpen: false,
    });
  },
  stopOverlayPlacement: () => set({ placingOverlayId: null, placingOverlayFitToView: false }),

  observationsLayerEnabled: true,
  observationsWhilePlanning: true,
  setObservationsVisible: (visible) =>
    set((s) => {
      const planFocus = s.planPanelOpen || s.viewedPlanId != null;
      return planFocus
        ? { observationsWhilePlanning: visible }
        : { observationsLayerEnabled: visible };
    }),

  hasSeenCreateCoachmark: null,
  hydrateCreateCoachmark: async () => {
    try {
      const v = await AsyncStorage.getItem(COACHMARK_KEY);
      set({ hasSeenCreateCoachmark: v === '1' });
    } catch {
      set({ hasSeenCreateCoachmark: false });
    }
  },
  markCreateCoachmarkSeen: () => {
    set({ hasSeenCreateCoachmark: true });
    AsyncStorage.setItem(COACHMARK_KEY, '1').catch(() => {});
  },

  hasSeenObservationGuidance: null,
  hydrateObservationGuidance: async () => {
    try {
      const v = await AsyncStorage.getItem(OBSERVATION_GUIDANCE_KEY);
      set({ hasSeenObservationGuidance: v === '1' });
    } catch {
      set({ hasSeenObservationGuidance: false });
    }
  },
  markObservationGuidanceSeen: () => {
    set({ hasSeenObservationGuidance: true });
    AsyncStorage.setItem(OBSERVATION_GUIDANCE_KEY, '1').catch(() => {});
  },

  observationGuidanceNext: null,
  setObservationGuidanceNext: (path) => set({ observationGuidanceNext: path }),

  hasSeenMapPaths: null,
  hydrateMapPaths: async () => {
    try {
      const v = await AsyncStorage.getItem(MAP_PATHS_KEY);
      set({ hasSeenMapPaths: v === '1' });
    } catch {
      set({ hasSeenMapPaths: false });
    }
  },
  markMapPathsEngaged: () => {
    set({ hasSeenMapPaths: true });
    AsyncStorage.setItem(MAP_PATHS_KEY, '1').catch(() => {});
  },

  mapPathsOverlayRequested: false,
  requestMapPathsOverlay: () => set({ mapPathsOverlayRequested: true }),
  clearMapPathsOverlayRequest: () => set({ mapPathsOverlayRequested: false }),

  arrivedViaPlanLink: false,
  markArrivedViaPlanLink: () => set({ arrivedViaPlanLink: true }),

  mapSearchFocusRequested: false,
  requestMapSearchFocus: () => set({ mapSearchFocusRequested: true }),
  clearMapSearchFocusRequest: () => set({ mapSearchFocusRequested: false }),

  pendingProperty: hydratePendingProperty(),
  pendingModel: hydratePendingModel(),
  setPendingModel: (checklistId) => {
    set({ pendingModel: checklistId });
    writeSessionJson(PENDING_MODEL_KEY, checklistId);
  },
  setPendingProperty: (pending) => {
    set({ pendingProperty: pending });
    writeSessionJson(PENDING_PROPERTY_KEY, pending);
  },
  previewEvaluation: hydratePreviewEvaluation(),
  setPreviewEvaluation: (preview) => {
    set({ previewEvaluation: preview });
    writeSessionJson(PREVIEW_EVALUATION_KEY, preview);
  },
  pendingAudience: hydratePendingAudience(),
  setPendingAudience: (audience) => {
    set({ pendingAudience: audience });
    writeSessionJson(PENDING_AUDIENCE_KEY, audience);
  },
  clearPendingAudience: () => {
    set({ pendingAudience: null });
    writeSessionJson(PENDING_AUDIENCE_KEY, null);
  },

  clearPropertyJourney: (structureId) => {
    const { pendingProperty, previewEvaluation, pendingModel } = get();
    if (pendingModel) {
      set({ pendingModel: null });
      writeSessionJson(PENDING_MODEL_KEY, null);
    }
    if (pendingProperty?.structureId === structureId) {
      set({ pendingProperty: null });
      writeSessionJson(PENDING_PROPERTY_KEY, null);
    }
    if (previewEvaluation?.structureId === structureId) {
      set({ previewEvaluation: null });
      writeSessionJson(PREVIEW_EVALUATION_KEY, null);
    }
  },

  recentlyViewedStructures: [],
  recordStructureView: (structure) =>
    set((s) => ({
      recentlyViewedStructures: [
        structure,
        ...s.recentlyViewedStructures.filter((x) => x.id !== structure.id),
      ].slice(0, RECENTLY_VIEWED_CAP),
    })),

  reset: () => {
    // The map's bottom sheet increments _hideCount when a structure is
    // selected; if sign-out happens before the sheet closes, the count
    // would be > 0 when the user lands back on the map (route guard
    // redirects to /(tabs)), leaving the floating tab bar invisible until
    // a hard refresh.
    set({
      _hideCount: 0,
      tabBarVisible: true,
      activeSheet: null,
      addPropertyInitialQuery: '',
      toast: null,
      dockedPostId: null,
      composerDockOpen: false,
      composerPin: null,
      showSaveAccountToast: false,
      recentlyViewedStructures: [],
      mapPathsOverlayRequested: false,
      arrivedViaPlanLink: false,
      observationGuidanceNext: null,
      fhszOverlayEnabled: false,
      fireHistoryEnabled: false,
      firewiseEnabled: false,
      fireSafeCouncilsEnabled: false,
      enabledOverlayIds: [],
      placingOverlayId: null,
      placingOverlayFitToView: false,
      observationsLayerEnabled: true,
      observationsWhilePlanning: true,
      layersPanelOpen: false,
      peoplePanelOpen: false,
      programPanelOpen: false,
      programVendorsRequested: false,
      mapKeyOverride: null,
      slideshowCloseRequestId: 0,
      planPanelOpen: false,
      planDrawTool: null,
      planStrokeActive: false,
      viewedPlanId: null,
      selectedPlanFeatureId: null,
      planMultiSelection: [],
      planBundleRenamingId: null,
      lastPlanCopy: null,
      planCopyMenuFeatureId: null,
      planRowMenuFeatureId: null,
      planFeatureContextMenu: null,
      hoveredPlanFeatureId: null,
      hoveredPlanBundleId: null,
      planListDrag: null,
      pendingPlanFeatureFocus: null,
      planPanelOpenRequestId: 0,
      pendingPlanAssignFocus: null,
      pendingPlanCompanion: null,
      planBundleSelection: null,
      planBundleEditingId: null,
      selectedPlanBundleId: null,
      pendingPlanBundleFocus: null,
    });
    // The plan subscriptions are keyed on the signed-in user; drop them
    // with the rest of the session state.
    useDefensePlansStore.getState().reset();
    // Same reasoning: the overlay subscription is keyed on areaId+moderator,
    // both of which can change across sign-out/sign-in.
    useMapOverlaysStore.getState().reset();
    // The People directory holds real phone numbers and emails — same
    // areaId-keyed subscription shape as overlays above, and the same
    // reason to drop it rather than let it linger for the next user on
    // this device.
    useAreaContactsStore.getState().reset();
    // Uploaded leader lists are private to the account that uploaded them:
    // drop the listeners and every cached person on sign-out.
    usePeopleListsStore.getState().reset();
    // The vendor directory holds reps' business emails and phones, and a
    // moderator's feed includes unapproved docs: stop the listeners and
    // drop every cached doc so the next account starts clean.
    useVendorsStore.getState().reset();
    // Belt-and-suspenders alongside placingOverlayId above: the working
    // corners this backs are meaningless without a session to save them
    // into, and usePlanMapPlacementLayer's own cleanup (keyed off
    // placingOverlayId going null) would clear it anyway — this just
    // doesn't make that the only path.
    usePlanMapPlacementStore.getState().clear();
    // Sign-out is the natural reset point for the "save your account"
    // nudge — the next user on this device may want to see it.
    get().resetSaveNudgeDismissal();
  },
}));

/**
 * Widest currently-registered right-docked panel width (see dockInsets
 * above), or 0 when nothing is docked. Every DockedPanel instance
 * registers itself on mount, so this covers the structure detail panel,
 * the defense plan editor and the observation presentation alike with no
 * per-panel knowledge here.
 */
export function selectRightDockInset(s: UIState): number {
  return maxDockEdge(s.dockInsets);
}

export function useRightDockInset(): number {
  return useUIStore(selectRightDockInset);
}

/**
 * Widest currently-registered left-docked panel width (see leftDockInsets
 * above), or 0 when nothing is docked there — mirrors selectRightDockInset.
 * Today only the defense plan editor/viewer register here, but this reads
 * the registry the same id-agnostic way so a future left-docked surface
 * needs no changes here either.
 */
export function selectLeftDockInset(s: UIState): number {
  return maxDockEdge(s.leftDockInsets);
}

/** Farthest edge any registered dock reaches from its side: max(offset + width). */
function maxDockEdge(insets: Record<string, DockInset>): number {
  let edge = 0;
  for (const { width, offset } of Object.values(insets)) edge = Math.max(edge, offset + width);
  return edge;
}

export function useLeftDockInset(): number {
  return useUIStore(selectLeftDockInset);
}

/**
 * True whenever MapPathsOverlay is (or should be) on screen: normal
 * first-visit hydration state (`hasSeenMapPaths !== true` — covers both
 * "never engaged" and "still hydrating") OR a Guidance-tab reopen request —
 * OR `arrivedViaPlanLink`, which holds this true even though MapPathsOverlay
 * itself won't actually render (it gates its own auto-show off that flag
 * directly, in its own file). The plan viewer owns the screen on that visit
 * the same way the welcome overlay would, so surfaces that need to stand
 * aside for one need to stand aside for the other. MapSignInOverlay and
 * CreatePostCoachmark both suppress themselves off of this exact formula so
 * neither stacks with whichever of the two is actually up — read this
 * selector rather than duplicating the boolean logic in either component.
 */
export function selectMapPathsOverlayActive(state: UIState): boolean {
  return (
    state.arrivedViaPlanLink || state.hasSeenMapPaths !== true || state.mapPathsOverlayRequested
  );
}

export function useMapPathsOverlayActive(): boolean {
  // Field mode never mounts MapPathsOverlay, so only a plan-link arrival
  // still keeps the corner chips and coachmark out of the way.
  const fieldMode = useFieldMode();
  return useUIStore(fieldMode ? (s) => s.arrivedViaPlanLink : selectMapPathsOverlayActive);
}

/**
 * Whether observation markers/clusters should be drawn right now — the
 * mode-aware read for `observationsLayerEnabled`/`observationsWhilePlanning`
 * above. `planFocus` mirrors the condition useDefensePlanLayer.web.ts uses
 * to toggle PLAN_FOCUS_CLASS: a plan is open for drawing, or one is being
 * viewed (own or shared).
 */
export function selectObservationsVisible(state: UIState): boolean {
  const planFocus = state.planPanelOpen || state.viewedPlanId != null;
  return planFocus ? state.observationsWhilePlanning : state.observationsLayerEnabled;
}

export function useObservationsVisible(): boolean {
  return useUIStore(selectObservationsVisible);
}

/**
 * Whether the desktop map key should be drawn at all right now — the
 * mode-aware read for `mapKeyOverride` above. Unset (null) follows
 * `planPanelOpen`, the same "collapsed while editing" default the key
 * always had; an explicit press of the rail's Key row wins either way.
 * Phone/tablet ignore this (MapLegend's own local expand/collapse state
 * handles the chip there) — desktop-only concept.
 */
export function selectMapKeyShown(state: UIState): boolean {
  return state.mapKeyOverride ?? !state.planPanelOpen;
}

export function useMapKeyShown(): boolean {
  return useUIStore(selectMapKeyShown);
}
